import { useCallback, useEffect, useState } from 'react';
import {
  boardApi,
  directory,
  pinned,
  type BadgeSummary,
  type Branding,
  type Category,
  type Message,
  type PinnedNotice,
  type Site,
} from '../lib/api.ts';
import { Mark } from './Mark.tsx';
import { MessageList } from './MessageList.tsx';
import { Composer } from './Composer.tsx';
import { InstallGuide } from './InstallGuide.tsx';
import { needsIosInstall } from '../lib/install.ts';
import { NotifyToggle } from './NotifyToggle.tsx';
import { Notices } from './Notices.tsx';
import { Admin } from './Admin.tsx';
import { PinnedBoard } from './PinnedBoard.tsx';
import { Requests } from './Requests.tsx';
import { Overdue } from './Overdue.tsx';
import { Search } from './Search.tsx';
import { RaiseRequest } from './RaiseRequest.tsx';
import { SiteChat } from './SiteChat.tsx';
import { StaffChat } from './StaffChat.tsx';
import { archiveMessage, restoreMessage, flushOutbox, queued } from '../lib/api.ts';
import { chat as chatApi, counts, overdue, deviceToken,
  type ChatRoom as ChatRoomRow } from '../lib/api.ts';
import { emitLive, onLive } from '../lib/live.ts';
import { chime } from '../lib/chime.ts';
import { connectLive, type LiveState } from '../lib/live.ts';
import { Assistant } from './Assistant.tsx';
import { Billing } from './Billing';
import { Help } from './Help.tsx';
import { NodeInfo } from './NodeInfo';
import { PAGE, Pager } from './Pager';
import { Icon } from './Icon';
import { Picker } from './Picker';
import { Shell } from './Shell';
import { LiveDot } from './LiveDot.tsx';

interface Props {
  readonly branding: Branding | null;
  readonly onSignOut: () => void;
}

/** One line per section, saying what it is for rather than repeating
    its own name back at the reader. */
const PAGE_BLURB: Record<string, string> = {
  board: 'Notices and messages for this site.',
  notices: 'Send to every site at once, and see who opened it.',
  search: 'Across every site and every month.',
  chat: 'Talk to head office.',
  // The assistant is not talking to head office; they are head
  // office talking to sites.

  requests: 'Faults and requests raised from a counter.',
  overdue: 'Past its response time, or still unacknowledged.',
  billing: 'Your invoices and what is outstanding.',
  help: 'How everything works. Print this for the counter.',
  admin: 'Sites, nodes, categories and people.',
  'admin:sites': 'Locations, and the nodes enrolled at each of them.',
  'admin:people': 'Who can sign in, what they cover, and how.',
  'admin:cats': 'The channels a site files things under.',
  'admin:brand': 'Your name, logo and how the app presents itself.',
};

export const Board = ({ branding, onSignOut }: Props) => {
  const [sites, setSites] = useState<Site[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [siteId, setSiteId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [badges, setBadges] = useState<BadgeSummary | null>(null);
  const [pins, setPins] = useState<PinnedNotice[]>([]);

  // /badges is device-only, so its success is the cheapest reliable
  // signal of which kind of principal is signed in. Avoids a /me call
  // on every load just to pick which controls to render.
  // An injected token is definitive: this is a shop-floor screen, not
  // a person. Probing /badges would let a stale admin cookie in the
  // same webview render the whole management surface on a counter PC.
  const [isDevice, setIsDevice] = useState<boolean | null>(
    deviceToken() ? true : null,
  );
  const [attention, setAttention] = useState(0);
  const [live, setLive] = useState<LiveState>('connecting');
  const [showInstall, setShowInstall] = useState(needsIosInstall());
  const chatOnly = branding?.chatOnly === true;
  const isAdmin = branding?.isAdmin === true;
  // Staff must never wonder whether a message sent. The count is the
  // whole point of a durable queue: silence would look identical to
  // success.
  const [pending, setPending] = useState(queued());

  useEffect(() => {
    const tick = () => {
      if (navigator.onLine) {
        void flushOutbox().then(() => setPending(queued()));
      } else {
        setPending(queued());
      }
    };

    const id = window.setInterval(tick, 5000);
    window.addEventListener('online', tick);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('online', tick);
    };
  }, []);


  // Land them where they can actually work rather than on a board tab
  // that is not there.
  useEffect(() => {
    if (chatOnly) setTab('chat');
  }, [chatOnly]);
  const [tab, setTab] = useState<'board' | 'requests' | 'chat' | 'search' | 'overdue' | 'notices' | 'admin' | 'billing' | 'help'>(
    'board',
  );

  // Sites and categories are already scoped server-side, so whatever
  // arrives is exactly what this principal may see.
  const loadDirectory = useCallback(() => {
    directory.sites().then((s) => {
      setSites(s);
      setSiteId((current) => current || s[0]?.id || '');
    });
    directory.categories().then((c) => {
      setCategories(c);
      setCategoryId((current) => current || c[0]?.id || '');
    });
  }, []);

  useEffect(loadDirectory, [loadDirectory]);

  const refresh = useCallback(() => {
    // An empty site is now a request, not a missing value: it means
    // every site this account covers.
    // "archived" is a view, not a category: it asks for the same
    // board with the opposite half of the messages.
    const showArchive = categoryId === 'archived';
    boardApi
      .messages(siteId || null, showArchive ? null : categoryId || null,
                showArchive)
      .then(setMessages).catch(() => undefined);
    boardApi
      .badges()
      .then((b) => {
        setBadges(b);
        setIsDevice(true);
      })
      .catch(() => {
        setBadges(null);
        // Never downgrade a token-authenticated shell to an account
        // view: the token is the stronger signal and a failed poll is
        // usually just a dropped connection.
        if (!deviceToken()) setIsDevice(false);
      });
    if (siteId) pinned(siteId).then(setPins)
      .catch(() => setPins([]));
    else setPins([]);

    // Devices get 403 here; the catch is the normal path for them.
    overdue()
      .then((d) =>
        setAttention(
          d.requests.length +
            d.uncovered.filter((g) => g.supervisors === 0).length,
        ),
      )
      .catch(() => setAttention(0));
  }, [siteId, categoryId]);

  useEffect(refresh, [refresh]);

  // Account sessions authenticate the socket with the cookie the
  // browser already sends; the site must be named because an admin
  // could be watching any of them. Device sockets use a bearer token
  // and are opened by the desktop shell, not here.
  useEffect(() => {
    // A device connects with its bearer token, an account with the
    // cookie plus an explicit site. Both paths land in the same hub.
    const token = deviceToken();

    // An assistant has no site and never will, so requiring one meant
    // the socket was never opened for them at all: the screen sat on
    // Offline and only ever updated on reload.
    if (!token && isDevice !== false) return;
    return connectLive({
      // An assistant subscribes to every site, not the one they
      // happen to be looking at.
      // A site when one is chosen, nothing when it is "all sites".
      // Requiring one meant an account on the all-sites view had no
      // socket at all, which is why messages arrived in one
      // direction and not the other.
      ...(token ? { token } : siteId ? { siteId } : {}),
      onState: setLive,
      onEvent: (e) => {
        // Revocation. The shell clears its credential store when the
        // socket closes 4403; reloading returns the browser case to
        // sign-in rather than a board it can no longer refresh.
        if (e.type === 'force_logout') {
          window.location.reload();
          return;
        }
        if (e.type === 'message' && e.siteId === siteId) {
          chime();
          refresh();
        }

        // Chat counts live in the same badge payload, and refreshing
        // only on board messages left the chat badge frozen at
        // whatever it was when the page loaded.
        if (e.type === 'chat') {
          chime();
          refresh();
        }
        // Chat lives further down the tree and needs the same
        // events without opening a second socket.
        emitLive(e);
      },
    });
  }, [chatOnly ? '' : siteId, isDevice, chatOnly, refresh]);

  /**
   * Deliberate open. Wired to a button press only -- never to scroll,
   * focus or visibility, which on an always-on screen would mark
   * everything read on render.
   */
  const open = async (id: string) => {
    await boardApi.open(id).catch(() => undefined);
    refresh();
  };

  const site = sites.find((s) => s.id === siteId);
  const category = categories.find((c) => c.id === categoryId);
  const name = branding?.identity?.product_name ?? branding?.name ?? '';

  // Filters the board to unread HQ notices across every category. A
  // count that does not take you anywhere is worse than no count.
  const [unreadOnly, setUnreadOnly] = useState(false);
  // Set when arriving from a search hit, so the request opens expanded.
  const [focusRequest, setFocusRequest] = useState('');
  const [raising, setRaising] = useState<Message | null>(null);

  const countFor = (id: string): number =>
    badges?.categories.find((c) => c.categoryId === id)?.count ?? 0;


  /**
   * One definition, two presentations.
   *
   * A counter PC is a 420px window on shop-floor hardware, where a
   * row of touch targets across the top is right. A supervisor is on
   * a laptop, where a rail down the side keeps every destination
   * visible. Same list either way, so a tab cannot exist in one
   * presentation and not the other.
   */

  /**
   * Unread per site, for the assistant's menu. The room list already
   * counts this; it was simply never asked for outside the chat
   * screen, so a site with a waiting reply looked like a quiet one.
   */
  const [page, setPage] = useState(0);
  // Accounts do not get /badges, so their waiting counts come
  // from the endpoint that answers the same question at their
  // own scope.
  const [acct, setAcct] = useState<
    { chatWaiting: number; repliesWaiting: number } | null>(null);
  const [about, setAbout] = useState(false);
  const [chatRooms, setChatRooms] = useState<ChatRoomRow[]>([]);
  useEffect(() => {
    if (!chatOnly) return;
    const pull = () => {
      chatApi.rooms().then(setChatRooms).catch(() => undefined);
    };
    pull();
    // Polled as well as pushed. Call Connect sits open all day, so the
    // socket will drop at some point, and a rail whose unread counts
    // quietly froze is worse than one a few seconds behind.
    const id = window.setInterval(pull, 20000);
    const off = onLive((e) => {
      if (e.type === 'chat') pull();
    });
    return () => {
      window.clearInterval(id);
      off();
    };
  }, [chatOnly]);

  // An assistant navigates by site, so the sites are the menu. A
  // single "Chat" item above a list of sites in the page was one
  // level of navigation too many.
  const navItems = chatOnly ? sites.map((s) => ({
    id: 'site:' + s.id,
    icon: 'chat',
    label: s.name,
    show: true,
    count: chatRooms.find((r) => r.siteId === s.id && !r.closedAt)
      ?.unread ?? 0,
    children: [] as Array<{ id: string; label: string }>,
  })) : [
    // Categories are the client's own taxonomy, so the submenu is
    // built from their data rather than a fixed list. All first,
    // because that is where a board should open.
    { id: 'board', icon: 'board', label: 'Board', show: !chatOnly,
      count: 0,
      children: [
        { id: 'board', label: 'All' },
        ...categories.map((c) => ({
          id: 'board:' + c.id,
          label: c.name,
          // Unread counts belong to a device. An account never
          // marks a site's notice as read, so every category would
          // show its whole history as outstanding.
          count: isDevice === true
            ? badges?.categories
                ?.find((b) => b.categoryId === c.id)?.count ?? 0
            : 0,
        })),
        // Last, after the live categories: it is a different kind of
        // thing, not another category, and admins only.
        ...(isAdmin
          ? [{ id: 'board:archived', label: 'Archived', muted: true }]
          : []),
      ] },
    { id: 'notices', icon: 'send', label: 'Send a notice', count: 0,
      show: isDevice === false && !chatOnly },

    { id: 'chat', icon: 'chat', label: 'Chat', show: !chatOnly,
      count: badges?.chatWaiting ?? acct?.chatWaiting ?? 0 },
    // A chat-only account answers messages. Tickets are a site's
    // workflow, and the server refuses them anyway.
    { id: 'requests', icon: 'ticket', label: 'Tickets',
      show: !chatOnly,
      count: badges?.repliesWaiting
        ?? acct?.repliesWaiting ?? 0 },
    { id: 'overdue', icon: 'alert', label: 'Needs attention', count: attention,
      show: isDevice === false && !chatOnly },
    // Billing is for the person who pays, which is the admin
    // account, never a counter screen.
    { id: 'billing', icon: 'card', label: 'Billing', count: 0,
      show: isDevice === false && !chatOnly },
    // Settings had four panes as tabs inside the page, which meant
    // three of them were invisible until you were already there.
    { id: 'admin', icon: 'cog', label: 'Settings', count: 0,
      show: isDevice === false && !chatOnly,
      children: [
        { id: 'admin:sites', label: 'Sites and nodes' },
        { id: 'admin:people', label: 'People' },
        { id: 'admin:cats', label: 'Categories' },
        { id: 'admin:brand', label: 'Branding' },
      ] },
    // Truly last: needed rarely, and it should not push the
    // daily items down the list.
    { id: 'help', icon: 'info', label: 'Guide', show: true,
      count: 0 },
  ].filter((n) => n.show);


  /**
   * Header controls that only some accounts get.
   *
   * A device has no session to end, and signing one out would strand
   * a counter screen nobody is standing at. Revocation is an admin
   * action taken from the other side.
   */

  /**
   * Site switcher.
   *
   * A board belongs to one site, so this scopes everything below it.
   * Shown only when there is a choice: a single-site client, and
   * every counter PC, has exactly one and needs no control that can
   * only be set to where they already are.
   */
  // An assistant navigates by site inside the chat screen itself,
  // so a site switcher above a one-item menu is two controls doing
  // the same job.
  const navTop = !chatOnly && sites.length > 1 ? (
    <div className="siteSwitch">
      <Picker
        label="Site"
        value={siteId}
        onChange={setSiteId}
        options={[
          { value: '', label: 'All sites' },
          ...sites.map((s) => ({ value: s.id, label: s.name })),
        ]}
      />
    </div>
  ) : null;


  /**
   * The timeline, minus anything already on the pinned board above
   * it. A pinned notice was appearing twice on the same screen, which
   * reads as two notices and makes the board look like it duplicated
   * the message.
   */

  useEffect(() => {
    if (isDevice !== false) return;
    const pull = () => {
      counts().then(setAcct).catch(() => undefined);
    };
    pull();
    const off = onLive((e) => {
      if (e.type === 'chat' || e.type === 'request') pull();
    });
    const id = window.setInterval(pull, 60000);
    return () => {
      off();
      window.clearInterval(id);
    };
  }, [isDevice]);

  const brandMark = (
        branding?.identity?.logo_version ? (
          <img
            className={
              branding.identity.logo_plate
                ? 'logoPlate headerLogo'
                : 'headerLogo'
            }
            src={`/branding/logo?v=${branding.identity.logo_version}`}
            alt={branding?.name ?? ''}
          />
        ) : (
          <Mark size={26} />
        )
  );

  const archiveView = categoryId === 'archived';
  const pinnedIds = new Set(pins.map((p) => p.id));

  const visible = messages
    .filter((m) => !pinnedIds.has(m.id))
    .filter((m) => !unreadOnly
      || (!m.opened_at && !m.sender_device_name));

  const headerExtra = (
    <>
      {pending > 0 && (
        <span className="queued mono">{pending} queued</span>
      )}
      {isDevice === false && !chatOnly && (
        <button
          className="iconBtn"
          onClick={() => setTab('search')}
          title="Search"
          aria-label="Search"
        >
          <Icon name="search" />
        </button>
      )}
      <LiveDot state={live} />
      <NotifyToggle />
      {isDevice === false && !chatOnly && (
        <button onClick={onSignOut}>Sign out</button>
      )}
    </>
  );

  // A sub-pane like "admin:sites" is not a top level item, so
  // looking only at the parents fell through to Board on every
  // Settings screen.
  const parent = navItems.find((n) => n.id === tab);
  const child = navItems
    .flatMap((n) => n.children ?? [])
    .find((c) => c.id === tab);

  const pageTitle = child?.label ?? parent?.label
    ?? (tab.startsWith('admin') ? 'Settings' : 'Board');
  const pageBlurb = categoryId === 'archived'
    ? 'Taken off the board. Nothing is deleted, and read coverage '
      + 'for these notices is still accurate.'
    : PAGE_BLURB[tab];

  return (
    <Shell
      brand={brandMark}
      name={name}
      deviceName={badges?.deviceName ?? undefined}
      nav={navItems}
      active={chatOnly ? 'site:' + siteId
        : tab === 'board' && categoryId
          ? 'board:' + categoryId : tab}
      onNavigate={(id) => {
        // A board child carries the category with it, so picking one
        // in the sidebar is the same action as picking a chip.
        // A site is the whole navigation for an assistant.
        if (id.startsWith('site:')) {
          setSiteId(id.slice('site:'.length));
          setTab('chat');
          return;
        }
        if (id === 'board' || id.startsWith('board:')) {
          setPage(0);
          setCategoryId(id.slice('board:'.length));
          setTab('board');
          return;
        }
        setTab(id as typeof tab);
      }}
      title={pageTitle}
      fullHeight={chatOnly}
      blurb={pageBlurb}
      headerExtra={headerExtra}
      navTop={navTop}
      navTitle={chatOnly ? 'Sites' : 'Menu'}
      version={badges?.appVersion ?? undefined}
      onAbout={() => setAbout(true)}
      pageTop={navTop}
    >

      {tab === 'chat' ? (
        <main className="boardMain">
          {isDevice === true ? (
            <SiteChat categories={categories} />
          ) : chatOnly ? (
            <Assistant categories={categories} siteId={siteId} />
          ) : (
            <StaffChat siteId={siteId} assistant={chatOnly} />
          )}
        </main>
      ) : tab === 'search' ? (

        <main className="boardMain">
          <Search
            onOpenRequest={(id) => {
              setFocusRequest(id);
              setTab('requests');
            }}
            onOpenBoard={(s, c) => {
              setSiteId(s);
              setCategoryId(c);
              setTab('board');
            }}
          />
        </main>
      ) : tab === 'overdue' ? (
        <main className="boardMain">
          <Overdue
            onOpenRequest={(id) => {
              setFocusRequest(id);
              setTab('requests');
            }}
            onFixCoverage={() => setTab('admin:people' as typeof tab)}
          />
        </main>
      ) : tab === 'requests' ? (
        <main className="boardMain">
          <Requests
            canFile={isDevice === true}
            meId={branding?.userId}
            canTriage={isDevice === false}
            categories={categories}
            siteId={siteId}
            focusRequest={focusRequest}
          />
        </main>
      ) : tab === 'help' ? (
        <Help
          name={name}
          brand={brandMark}
          version={badges?.appVersion}
        />
      ) : tab === 'billing' ? (
        <main className="boardMain">
          <Billing />
        </main>
      ) : tab.startsWith('admin') ? (
        <main className="boardMain">
          <Admin
            sites={sites}
            categories={categories}
            onSitesChanged={loadDirectory}
            pane={(tab.split(':')[1] ?? 'sites') as
              'sites' | 'people' | 'cats' | 'brand'}
            meId={branding?.userId}
            />
        </main>
      ) : tab === 'notices' ? (
        <main className="boardMain">
          <Notices sites={sites} categories={categories} />
        </main>
      ) : (
      <>
      {/* One prominent count above the rail. Per-category badges say
          where; this says whether anything needs you at all, which is
          what someone walking past is asking. Steady, not flashing:
          animation on an always-on screen goes unseen within a week. */}
      {isDevice === true && (badges?.unreadNotices ?? 0) > 0 && (
        <button
          className={unreadOnly ? 'noticeFlag on' : 'noticeFlag'}
          onClick={() => setUnreadOnly(!unreadOnly)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 21 V4 h12 l-2.2 3.6 L17 11 H5" />
          </svg>
          <span>
            {badges?.unreadNotices} from head office
          </span>
        </button>
      )}

      <nav className="catRail">
        {/* All is first and selectable: on a wide screen there is room
            to show everything, and switching tabs to find one notice
            is work the screen size makes needless. */}
        <button
          className={categoryId ? 'cat' : 'cat on'}
          onClick={() => setCategoryId('')}
        >
          All
        </button>
        {categories.map((c) => {
          const n = countFor(c.id);
          return (
            <button
              key={c.id}
              className={c.id === categoryId ? 'cat on' : 'cat'}
              onClick={() => setCategoryId(c.id)}
            >
              <span>{c.name}</span>
              {n > 0 && <b className="badge">{n > 9 ? '9+' : n}</b>}
            </button>
          );
        })}
      </nav>

      {showInstall && (
        <InstallGuide onDismiss={() => setShowInstall(false)} />
      )}

      <main className="boardMain">
        <PinnedBoard
          notices={pins}
          onOpen={open}
          canRead={isDevice === true}
        />
        {/* Archive or restore, never both: in the archive the
            only sensible action is to put it back. */}
        <MessageList
          messages={archiveView
            ? visible.slice(page * PAGE, page * PAGE + PAGE)
            : visible}
          onOpen={open}
          canAttach={isDevice === true}
          canRead={isDevice === true}
          {...(isDevice === true ? { onRaise: setRaising } : {})}
          {...(isDevice === false && categoryId !== 'archived'
            ? {
                onArchive: (id: string) => {
                  void archiveMessage(id).then(refresh);
                },
              }
            : {})}
          {...(isAdmin && categoryId === 'archived'
            ? {
                onRestore: (id: string) => {
                  void restoreMessage(id).then(refresh);
                },
              }
            : {})}
        />
        {archiveView && (
          <Pager page={page} count={visible.length}
                 onPage={setPage} />
        )}
        {raising && (
          <RaiseRequest
            message={raising}
            categoryId={categoryId}
            onDone={() => {
              setRaising(null);
              refresh();
            }}
          />
        )}
        {site && category && (
          <Composer site={site} category={category} onSent={refresh} />
        )}
      </main>
      </>
      )}

      {about && (
        <NodeInfo
          deviceName={badges?.deviceName}
          siteName={site?.name}
          version={badges?.appVersion}
          publicIp={branding?.publicIp}
          tenantName={name}
          live={live}
          onClose={() => setAbout(false)}
        />
      )}
    </Shell>
  );
};
